export type CompartmentViolation =
  | { type: "HEAVY_ONLY"; compartmentId: string }
  | { type: "DOUBLE_BULKHEAD"; compartmentId: string; nextCompartmentId: string }
  | { type: "MAX_VOLUME"; compartmentId: string; volume: number; max: number }
  | { type: "MIN_VOLUME"; compartmentId: string; volume: number; min: number }
  | { type: "MIXED_COMPARTMENT"; compartmentId: string }
  | { type: "UNTYPED"; commodityId: string }
  | {
      type: "OVER_ASSIGNED";
      commodityId: string;
      assigned: number;
      ordered: number;
    };

type CompartmentInput = {
  identifier: string;
  capacity: number;
  minVolume?: number | null;
  maxVolume?: number | null;
  heavyOnly?: boolean | null;
  hasDoubleBulkheadAfter?: boolean | null;
};

type AssignmentInput = {
  compartmentId: string;
  commodityId: string;
  quantity: number;
};

/**
 * Validates a GIVEN placement of commodities into a trailer's compartments
 * against the TTMSD-1894 loading rules. Returns one violation per failing rule
 * instance. See docs/superpowers/plans/2026-06-18-ttmsd-1894-* for the rules.
 */
export function validateCompartmentPlacement(input: {
  compartments: CompartmentInput[];
  assignments: AssignmentInput[];
  productTypeByCommodity: Record<string, "HEAVY" | "LIGHT" | undefined>;
}): CompartmentViolation[] {
  const { compartments, assignments, productTypeByCommodity } = input;
  const violations: CompartmentViolation[] = [];

  // Aggregate per compartment.
  const volumeByCompartment = new Map<string, number>();
  const typesByCompartment = new Map<string, Set<"HEAVY" | "LIGHT">>();
  const distinctTypedCommodities = new Set<string>();
  const untypedCommodities = new Set<string>();
  let hasHeavy = false;

  for (const a of assignments) {
    if (a.quantity <= 0) continue;
    volumeByCompartment.set(
      a.compartmentId,
      (volumeByCompartment.get(a.compartmentId) ?? 0) + a.quantity
    );
    const type = productTypeByCommodity[a.commodityId];
    if (type === "HEAVY" || type === "LIGHT") {
      distinctTypedCommodities.add(a.commodityId);
      if (!typesByCompartment.has(a.compartmentId)) {
        typesByCompartment.set(a.compartmentId, new Set());
      }
      typesByCompartment.get(a.compartmentId)!.add(type);
      if (type === "HEAVY") hasHeavy = true;
    } else {
      untypedCommodities.add(a.commodityId);
    }
  }

  const hasTypedCommodities = distinctTypedCommodities.size > 0;
  const hasMultipleCommodities = distinctTypedCommodities.size + untypedCommodities.size >= 2;
  const multiProduct = hasTypedCommodities && hasMultipleCommodities;
  const enforceHeavyRules = multiProduct && hasHeavy;

  // R-UNTYPED (multi-product only).
  if (multiProduct) {
    for (const commodityId of Array.from(untypedCommodities)) {
      violations.push({ type: "UNTYPED", commodityId });
    }
  }

  // R-MIXED, R-MIN, R-MAX (always) — per loaded compartment.
  for (const compartment of compartments) {
    const volume = volumeByCompartment.get(compartment.identifier) ?? 0;
    if (volume <= 0) continue; // unloaded: no min, no max, no mix.

    const compartmentTypes = typesByCompartment.get(compartment.identifier);
    if (compartmentTypes && compartmentTypes.has("HEAVY") && compartmentTypes.has("LIGHT")) {
      violations.push({ type: "MIXED_COMPARTMENT", compartmentId: compartment.identifier });
    }

    const max = compartment.maxVolume ?? compartment.capacity;
    if (volume > max) {
      violations.push({ type: "MAX_VOLUME", compartmentId: compartment.identifier, volume, max });
    }

    const min = compartment.minVolume ?? 0;
    if (volume < min) {
      violations.push({ type: "MIN_VOLUME", compartmentId: compartment.identifier, volume, min });
    }
  }

  if (!enforceHeavyRules) {
    return violations;
  }

  // R-HEAVYONLY (TTMSD-1962): heavy in a non-heavy-only compartment is allowed
  // when air-gapped (double bulkhead) from every adjacent light compartment.
  for (let i = 0; i < compartments.length; i++) {
    const compartment = compartments[i];
    const compartmentTypes = typesByCompartment.get(compartment.identifier);
    if (!compartmentTypes?.has("HEAVY") || compartment.heavyOnly) continue;

    const prev = compartments[i - 1];
    const next = compartments[i + 1];
    const leftLightUnguarded =
      !!prev &&
      typesByCompartment.get(prev.identifier)?.has("LIGHT") === true &&
      !prev.hasDoubleBulkheadAfter;
    const rightLightUnguarded =
      !!next &&
      typesByCompartment.get(next.identifier)?.has("LIGHT") === true &&
      !compartment.hasDoubleBulkheadAfter;

    if (leftLightUnguarded || rightLightUnguarded) {
      violations.push({ type: "HEAVY_ONLY", compartmentId: compartment.identifier });
    }
  }

  // R-BULKHEAD — adjacency by array order (decision Q4).
  for (let i = 0; i < compartments.length - 1; i++) {
    const current = compartments[i];
    const next = compartments[i + 1];
    const currentTypes = typesByCompartment.get(current.identifier);
    const nextTypes = typesByCompartment.get(next.identifier);
    if (!currentTypes || !nextTypes) continue;

    const mixedAcross =
      (currentTypes.has("HEAVY") && nextTypes.has("LIGHT")) ||
      (currentTypes.has("LIGHT") && nextTypes.has("HEAVY"));
    if (mixedAcross && !current.hasDoubleBulkheadAfter) {
      violations.push({
        type: "DOUBLE_BULKHEAD",
        compartmentId: current.identifier,
        nextCompartmentId: next.identifier,
      });
    }
  }

  return violations;
}

type TrailerForValidation = {
  _id: string;
  compartments?: CompartmentInput[] | null;
};

/**
 * Validates the placement across the main trailer AND every additional trailer.
 *
 * Assignments are grouped per trailer (an assignment with no trailerId belongs
 * to the main trailer) and each trailer is validated against its OWN
 * compartments only — so two trailers that happen to share a compartment
 * identifier are never cross-matched, and an invalid placement on an additional
 * trailer is no longer silently skipped.
 */
export function validateAllTrailersPlacement(input: {
  mainTrailer?: TrailerForValidation | null;
  additionalTrailers?: TrailerForValidation[];
  assignments: (AssignmentInput & { trailerId?: string | null })[];
  productTypeByCommodity: Record<string, "HEAVY" | "LIGHT" | undefined>;
}): CompartmentViolation[] {
  const {
    mainTrailer,
    additionalTrailers = [],
    assignments,
    productTypeByCommodity,
  } = input;

  const trailers = [mainTrailer, ...additionalTrailers].filter(
    (t): t is TrailerForValidation => t != null
  );

  return trailers.flatMap((trailer) => {
    const isMain = mainTrailer != null && trailer._id === mainTrailer._id;
    const trailerAssignments = assignments.filter((a) =>
      a.trailerId ? a.trailerId === trailer._id : isMain
    );
    return validateCompartmentPlacement({
      compartments: trailer.compartments ?? [],
      assignments: trailerAssignments.map((a) => ({
        compartmentId: a.compartmentId,
        commodityId: a.commodityId,
        quantity: a.quantity,
      })),
      productTypeByCommodity,
    });
  });
}
